import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import type { Exact } from "@Helpers/types/exact";
import type {
  FetchFormEntriesRequest,
  FetchFormEntriesResponse,
  AddSectionToLibraryRequest,
  AddSectionToLibraryResponse,
  GetEntitesFromLibraryRequest,
  GetEntitesFromLibraryResponse,
  DeleteSectionsFromLibraryRequest,
  DeleteSectionsFromLibraryResponse,
  FormUserSettingsControllerRequest,
  FormUserSettingsControllerResponse,
  TipfUpdateRequest,
  TaskInspectionAndProgressFormsControllerResponse,
  FormFlowControllerRequest,
  FormFlowControllerResponse,
  GetTemplatesResponse,
  GetTemplateTypesResponse,
  TemplateDefaultRequest,
  SaveTemplateRequest,
  EditTemplateRequest,
  DeleteTemplateRequest,
  ArchiveTemplateRequest,
  UpdateUsedTemplateRequest,
  TemplateTypesDefaultRequest,
  InsertTemplateTypeRequest,
  UpdateTemplateTypeRequest,
  DeleteTemplateTypeRequest,
  SaveTaskInspectionAndProgressResponse,
  TipfSaveRequest,
  TipfDefaultRequest,
  TipfDeleteRequest,
  TipfArchiveRequest,
} from "@ServerTypes/forms/forms.schema";
// Runtime request schemas — the SAME Zod schemas the server enforces on req.body via
// registerRoutes/validateRequest (single source of truth). Imported as runtime values (not `type`)
// so each dedicated action fail-fast validates (.parse throws) its payload before the network call.
// Because the server gates every request on these schemas, any payload it would accept already
// conforms here, so this cannot reject a currently-valid flow.
import {
  templateDefaultSchema,
  saveTemplateSchema,
  editTemplateSchema,
  deleteTemplateSchema,
  archiveTemplateSchema,
  updateUsedTemplateSchema,
  templateTypesDefaultSchema,
  insertTemplateTypeSchema,
  updateTemplateTypeSchema,
  deleteTemplateTypeSchema,
} from "@ServerTypes/forms/forms.schema";

// Each former action-dispatcher is split into dedicated endpoints. These maps route an `action` to
// its endpoint path; an unmapped/absent action falls back to the list endpoint.
const FORM_USER_SETTINGS_ACTION_PATHS: Record<string, string> = {
  createForm: "/forms/createFormFromUserSettings",
  deleteFormUserSettings: "/forms/deleteFormUserSettings",
  checkFormUserSettings: "/forms/checkFormUserSettings",
};
const FORM_FLOW_ACTION_PATHS: Record<string, string> = {
  update: "/forms/updateFormFlow",
  save: "/forms/saveFormFlow",
  delete: "/forms/deleteFormFlow",
  duplicate: "/forms/duplicateFormFlow",
  addToLibrary: "/forms/addFormFlowToLibrary",
  addFromLibrary: "/forms/addFormFlowFromLibrary",
  archive: "/forms/archiveFormFlow",
};
// ── Task inspection/progress form INSTANCES: one dedicated function per endpoint, replacing the
//    `taskInspectionAndProgressFormsController` action-dispatcher (which resolved the path from
//    `data.action ?? data.value.action` at call time). Each function names its endpoint statically and
//    takes the request type of the schema THAT route validates against — `tipfDefaultSchema` /
//    `tipfUpdateSchema` / `tipfSaveSchema` / `tipfDeleteSchema` / `tipfArchiveSchema` in
//    packages/server/forms/forms.schema.ts — and returns that route's declared `responseSchema` type.
//    So the payload a caller may pass is exactly what its endpoint accepts, checked at the call site
//    instead of at runtime by a lookup that silently fell back to the list endpoint on a typo.
//
//    Note the request types are the per-action schemas, NOT the
//    `TaskInspectionAndProgressFormsControllerRequest` union the dispatcher took: the union let any
//    action's payload reach any path. ──

/** POST /forms/taskInspectionAndProgressFormsController — the read path (list, or one instance by `resourceId`). */
export async function getForm<T extends Exact<TipfDefaultRequest, T>>(data: T) {
  const response = await axiosWrapper.post<TaskInspectionAndProgressFormsControllerResponse>(
    `${getBackendUrl()}/forms/taskInspectionAndProgressFormsController`,
    data,
  );
  return response;
}
/** POST /forms/saveTaskInspectionAndProgress — creates a form instance; the only route that returns `id`. */
export async function saveForms<T extends Exact<TipfSaveRequest, T>>(data: T) {
  const response = await axiosWrapper.post<SaveTaskInspectionAndProgressResponse>(
    `${getBackendUrl()}/forms/saveTaskInspectionAndProgress`,
    data,
  );
  return response;
}
/**
 * POST /forms/updateTaskInspectionAndProgress — the multi-purpose per-instance mutation (fill an
 * answer, verify/submit/approve/reject/roll back, attach or remove WBS/BOQ/tasks/milestones/files).
 *
 * `abortController` is threaded through because `useForms` cancels the in-flight answer save for a
 * question when a newer keystroke supersedes it; the dispatcher accepted it for the same reason.
 */
export async function updateForm<T extends Exact<TipfUpdateRequest, T>>(data: T, abortController?: AbortController) {
  const response = await axiosWrapper.post<TaskInspectionAndProgressFormsControllerResponse>(
    `${getBackendUrl()}/forms/updateTaskInspectionAndProgress`,
    data,
    { signal: abortController?.signal },
  );
  return response;
}
/** POST /forms/deleteTaskInspectionAndProgress — soft-deletes instances the caller created. */
export async function deleteForms<T extends Exact<TipfDeleteRequest, T>>(data: T) {
  const response = await axiosWrapper.post<TaskInspectionAndProgressFormsControllerResponse>(
    `${getBackendUrl()}/forms/deleteTaskInspectionAndProgress`,
    data,
  );
  return response;
}
/** POST /forms/archiveTaskInspectionAndProgress — writes `isArchived.status` on the given instances. */
export async function archiveForms<T extends Exact<TipfArchiveRequest, T>>(data: T) {
  const response = await axiosWrapper.post<TaskInspectionAndProgressFormsControllerResponse>(
    `${getBackendUrl()}/forms/archiveTaskInspectionAndProgress`,
    data,
  );
  return response;
}
export type MultipartFile = Blob | { uri: string; name: string; type?: string };
// `TipfUpdateRequest`, not the whole controller union: this function posts to
// `/forms/updateTaskInspectionAndProgress` and nothing else, so the update variant is the only payload it
// can legitimately be handed. Generic over the union, `Exact` had to satisfy every other action's shape
// too, which is what forced the callers in useForms.tsx to assert their way past it.
export async function updateTaskInspectionAndProgressWithFile<T extends Exact<TipfUpdateRequest, T>>(
  data: T,
  file: MultipartFile,
  fileName: string,
  // A signal here rather than a controller: the JSON transport above is handed the controller the hook
  // holds, this one only ever needs to be told when that controller fires. Without it the multipart
  // branch of a write was the one path the supersede mechanism could not reach, so a stale file write
  // could land after the newer write that was meant to replace it.
  signal?: AbortSignal,
) {
  const formData = new FormData();
  formData.append("payload", JSON.stringify(data));
  if (file instanceof Blob) {
    formData.append("file", file, fileName);
  } else {
    // Native descriptor: the filename travels inside it, so the third argument does not apply.
    // React Native's own FormData implementation accepts this `{ uri, name, type }` shape; lib.dom
    // types `FormData.append` as `string | Blob` only, and there is no way to state the platform's
    // wider contract from here.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- RN FormData accepts a `{uri,name,type}` file descriptor (built in useForms.tsx:889); lib.dom's `append` only models `string | Blob`
    formData.append("file", file as unknown as Blob);
  }
  // Config carrying only `signal`: no headers are set here, so axios still derives the multipart
  // boundary from the `FormData` itself.
  const response = await axiosWrapper.post<TaskInspectionAndProgressFormsControllerResponse>(
    `${getBackendUrl()}/forms/updateTaskInspectionAndProgress`,
    formData,
    { signal },
  );
  return response;
}
export async function formUserSettingsController<T extends Exact<FormUserSettingsControllerRequest, T>>(data: T) {
  const action = (data as { action?: string }).action;
  const path = (action && FORM_USER_SETTINGS_ACTION_PATHS[action]) || "/forms/checkFormUserSettings";
  const response = await axiosWrapper.post<FormUserSettingsControllerResponse>(`${getBackendUrl()}${path}`, data);
  return response;
}

export async function fetchFormEntries<T extends Exact<FetchFormEntriesRequest, T>>(data: T) {
  const response = await axiosWrapper.post<FetchFormEntriesResponse>(`${getBackendUrl()}/forms/fetchFormEntries`, data);
  return response;
}
export async function formFlowController<T extends Exact<FormFlowControllerRequest, T>>(data: T) {
  const action = (data as { action?: string }).action;
  const path = (action && FORM_FLOW_ACTION_PATHS[action]) || "/forms/formFlowController";
  const response = await axiosWrapper.post<FormFlowControllerResponse>(`${getBackendUrl()}${path}`, data);
  return response;
}
// ── Templates: one dedicated function per endpoint (no generic dispatcher). Each fail-fast validates
//    its payload against the server request schema, then posts to its own dedicated endpoint. ──
export const getTemplates = async <T extends Exact<TemplateDefaultRequest, T>>(data: T) => {
  templateDefaultSchema.parse(data);
  return axiosWrapper.post<GetTemplatesResponse>(`${getBackendUrl()}/forms/getTemplates`, data);
};
export const saveTemplate = async <T extends Exact<SaveTemplateRequest, T>>(data: T) => {
  saveTemplateSchema.parse(data);
  return axiosWrapper.post<GetTemplatesResponse>(`${getBackendUrl()}/forms/saveTemplate`, data);
};
export const editTemplate = async <T extends Exact<EditTemplateRequest, T>>(data: T) => {
  editTemplateSchema.parse(data);
  return axiosWrapper.post<GetTemplatesResponse>(`${getBackendUrl()}/forms/editTemplate`, data);
};
export const deleteTemplate = async <T extends Exact<DeleteTemplateRequest, T>>(data: T) => {
  deleteTemplateSchema.parse(data);
  return axiosWrapper.post<GetTemplatesResponse>(`${getBackendUrl()}/forms/deleteTemplate`, data);
};
export const archiveTemplate = async <T extends Exact<ArchiveTemplateRequest, T>>(data: T) => {
  archiveTemplateSchema.parse(data);
  return axiosWrapper.post<GetTemplatesResponse>(`${getBackendUrl()}/forms/archiveTemplate`, data);
};
export const updateUsedTemplate = async <T extends Exact<UpdateUsedTemplateRequest, T>>(data: T) => {
  updateUsedTemplateSchema.parse(data);
  return axiosWrapper.post<GetTemplatesResponse>(`${getBackendUrl()}/forms/updateUsedTemplate`, data);
};

// ── Template types: one dedicated function per endpoint. ──
export const getTemplateTypes = async <T extends Exact<TemplateTypesDefaultRequest, T>>(data: T) => {
  templateTypesDefaultSchema.parse(data);
  return axiosWrapper.post<GetTemplateTypesResponse>(`${getBackendUrl()}/forms/getTemplateTypes`, data);
};
export const insertTemplateType = async <T extends Exact<InsertTemplateTypeRequest, T>>(data: T) => {
  insertTemplateTypeSchema.parse(data);
  return axiosWrapper.post<GetTemplateTypesResponse>(`${getBackendUrl()}/forms/insertTemplateType`, data);
};
export const updateTemplateType = async <T extends Exact<UpdateTemplateTypeRequest, T>>(data: T) => {
  updateTemplateTypeSchema.parse(data);
  return axiosWrapper.post<GetTemplateTypesResponse>(`${getBackendUrl()}/forms/updateTemplateType`, data);
};
export const deleteTemplateType = async <T extends Exact<DeleteTemplateTypeRequest, T>>(data: T) => {
  deleteTemplateTypeSchema.parse(data);
  return axiosWrapper.post<GetTemplateTypesResponse>(`${getBackendUrl()}/forms/deleteTemplateType`, data);
};

export const addSectionToLibrary = async <T extends Exact<AddSectionToLibraryRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<AddSectionToLibraryResponse>(
    `${getBackendUrl()}/forms/addSectionToLibrary`,
    data,
  );
  return response;
};
export const getEntitesFromLibrary = async <T extends Exact<GetEntitesFromLibraryRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<GetEntitesFromLibraryResponse>(
    `${getBackendUrl()}/forms/getEntitesFromLibrary`,
    data,
  );
  return response;
};

export const deleteSectionsFromLibrary = async <T extends Exact<DeleteSectionsFromLibraryRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<DeleteSectionsFromLibraryResponse>(
    `${getBackendUrl()}/forms/deleteSectionsFromLibrary`,
    data,
  );
  return response;
};
